import React, { useEffect } from 'react';
import styled from 'styled-components';

import { UiTypography } from '@/components/ui/Typography';

const PrideColors = [
  '#FF4431', // Red
  '#FC9A01', // Orange
  '#FFE246', // Yellow
  '#8DC62B', // Green
  '#0093D7', // Blue
  '#8521FF', // Purple
];

const LineFillWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const ProgressLineContainer = styled.div`
  width: 100%;
  height: 16px;
  background-color: #78788029;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: row;
`;

const ColorSegment = styled.div<{ color: string }>`
  height: 100%;
  background-color: ${({ color }) => color};
  width: 0%;
  transition: width 0.1s linear;
`;

const LoadingText = styled(UiTypography)`
  text-align: center;
  font-weight: bold;
`;

export const PrideStepLoading: React.FC<{
  duration?: number;
  onComplete?: () => void;
}> = ({ duration = 3, onComplete }) => {
  const segmentsRef = React.useRef<(HTMLDivElement | null)[]>([]);
  const animationFrameId = React.useRef<number>();
  const startTimeRef = React.useRef<number>();

  useEffect(() => {
    const progressTextElement = document.querySelector(
      '.loading-text-progress',
    );
    const totalSegments = PrideColors.length;
    const segmentWidthPercent = 100 / totalSegments;

    const step = (now: number) => {
      if (!startTimeRef.current) startTimeRef.current = now;

      const elapsed = (now - startTimeRef.current) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const progressPercent = Math.round(progress * 100);
      const totalWidth = progress * 100;

      PrideColors.forEach((_, index) => {
        const fullWidth = (index + 1) * segmentWidthPercent;
        let widthPercent = 0;
        if (totalWidth >= fullWidth) {
          widthPercent = segmentWidthPercent;
        } else if (totalWidth > index * segmentWidthPercent) {
          widthPercent = totalWidth - index * segmentWidthPercent;
        }
        if (segmentsRef.current[index]) {
          segmentsRef.current[index]!.style.width = `${widthPercent}%`;
        }
      });

      if (progressTextElement) {
        progressTextElement.textContent = `Loading ${progressPercent}%`;
      }

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(step);
      } else {
        if (onComplete) onComplete();
      }
    };

    animationFrameId.current = requestAnimationFrame(step);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      startTimeRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LineFillWrapper>
      <ProgressLineContainer>
        {PrideColors.map((color, i) => (
          <ColorSegment
            key={color}
            color={color}
            ref={(el: HTMLDivElement | null) => (segmentsRef.current[i] = el)}
          />
        ))}
      </ProgressLineContainer>
      <LoadingText className="loading-text-progress" variant="label-large">
        Loading 0%
      </LoadingText>
    </LineFillWrapper>
  );
};
